import { useRef } from 'react';
import { Formik, Form, type FormikProps } from 'formik';
import { Text } from '@rsos/base-components';
import { Box, Button, Select } from '@rsos/components/capstone/base';
import type { EtlTransportResponse } from '@rsos/reduxDataStore/src/features/resourceRequests';
import {
  Label,
  ButtonContainer,
  TextArea,
  Loader,
} from '../AirAmbulanceForm/AirAmbulanceFormStandalone.styles';
import LocationSection from '../AirAmbulanceForm/components/LocationSection';
import {
  REQUEST_REASONS,
  FORM_FIELDS,
  DEFAULT_BUTTON_OPTIONS,
} from '../AirAmbulanceForm/constants';
import type { SelectOption } from '../types';
import { LabeledButtonGroup } from './LabeledButtonGroup';
import { LabeledInput } from './LabeledInput';
import { useEditFormSubmit } from './hooks/useEditFormSubmit';
import {
  useEditFormValues,
  type EditFormValues,
} from './hooks/useEditFormValues';

interface AirAmbulanceEtlEditFormProps {
  transport: EtlTransportResponse;
}

function AirAmbulanceEtlEditForm({
  transport,
}: AirAmbulanceEtlEditFormProps): JSX.Element {
  const { handleSubmit, handleCancel, isLoading, error } =
    useEditFormSubmit(transport);
  const initialValues = useEditFormValues(transport);
  const formRef = useRef<FormikProps<EditFormValues>>(null);

  const launchTypeField = FORM_FIELDS.find(f => f.field === 'launch_type');
  const disabledButtonFields = FORM_FIELDS.filter(
    f => f.field !== 'launch_type',
  );

  function notEditableTooltip(fieldName: string): string {
    return `${fieldName} is set by the original ETL request and cannot be changed.`;
  }

  return (
    <Box>
      <Formik<EditFormValues>
        innerRef={formRef}
        enableReinitialize={false}
        initialValues={initialValues}
        onSubmit={handleSubmit}
      >
        {({ values, touched, handleChange, handleBlur, setFieldValue }) => (
          <Form>
            <Box mx="5px">
              <LocationSection
                values={values}
                touched={touched}
                handleBlur={handleBlur}
                handleChange={handleChange}
                setFieldValue={setFieldValue}
                formRef={formRef}
                initialCoordinates={initialValues.pickup_coordinates}
                requestId={transport.gmr_transport_id}
              />

              <LabeledInput
                label="Ground Contact Radio Channel & Unit Name*"
                name="ground_contact"
                value={values.ground_contact}
                setFieldValue={setFieldValue}
                onChange={handleChange}
                onBlur={handleBlur}
                placeholder="Ground contact"
              />

              <Box pb={3} title={notEditableTooltip('Incident/Patient Info')}>
                <Label isDisabled>
                  <Text size="small" color="primaryText">
                    Incident/Patient Info*
                  </Text>
                </Label>
                <TextArea
                  name="patient_info"
                  value={values.patient_info}
                  disabled
                  rows={3}
                  style={{ opacity: 0.5, cursor: 'not-allowed' }}
                />
              </Box>

              <Box pb={3}>
                <Label>
                  <Text size="small" color="primaryText">
                    Request Reason
                  </Text>
                </Label>
                <Select
                  name="request_reason"
                  classNamePrefix="request_reason"
                  options={REQUEST_REASONS}
                  placeholder="Please select"
                  isClearable={false}
                  isSearchable={false}
                  value={values.request_reason}
                  onChange={(selectedOption: SelectOption | null) => {
                    setFieldValue('request_reason', selectedOption);
                  }}
                />
              </Box>

              {launchTypeField && (
                <LabeledButtonGroup
                  label={launchTypeField.label}
                  options={launchTypeField.options || DEFAULT_BUTTON_OPTIONS}
                  selectedValue={values.launch_type}
                  disabled
                  title={notEditableTooltip('Launch Type')}
                />
              )}

              {disabledButtonFields.map(
                ({ label, field, options = DEFAULT_BUTTON_OPTIONS }) => (
                  <LabeledButtonGroup
                    key={field}
                    label={label}
                    options={options}
                    selectedValue={values[field]}
                    disabled
                    title={notEditableTooltip(label)}
                  />
                ),
              )}

              <Box>
                {error && (
                  <Box
                    mb={1}
                    p={3}
                    bg="lightDanger"
                    borderRadius="4px"
                    role="alert"
                  >
                    <Text color="background" size="small">
                      {error}
                    </Text>
                  </Box>
                )}
                <ButtonContainer horizontal>
                  <Button
                    grow
                    btnType="tertiary"
                    type="button"
                    mx={0}
                    onClick={handleCancel}
                    disabled={isLoading}
                  >
                    Cancel
                  </Button>
                  <Button
                    grow
                    type="submit"
                    btnType="primary"
                    ml={2}
                    disabled={isLoading}
                  >
                    {isLoading ? <Loader /> : 'Save Changes'}
                  </Button>
                </ButtonContainer>
              </Box>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
}

export default AirAmbulanceEtlEditForm;
